/**
 * Title screen and boot presentation.
 *
 * The build booted straight into gameplay with a flash of black. That is the
 * single clearest "this is a tech demo" tell there is: the first two seconds
 * of a commercial game are a title, and we had none.
 *
 * The backdrop is the live 3D scene rather than a static image - the camera
 * drifts slowly over the neighbourhood at golden hour behind the menu, which
 * costs nothing extra to render and immediately says "this is a world, not a
 * screen".
 */
import { formatMoney } from '../../sim/core/money.js';

export interface SaveSlotInfo {
  slot: string;
  label: string;
  detail: string;
  empty: boolean;
  corrupt: boolean;
}

export interface TitleScreenHooks {
  onNewGame(): void;
  onContinue(slot: string): void;
  onLoad(slot: string): void;
  listSaves(): SaveSlotInfo[];
  getSettings(): GameSettings;
  setSettings(next: Partial<GameSettings>): void;
  onQuit?(): void;
  sound?: { ui(kind: 'click' | 'open' | 'close' | 'error'): void };
}

export interface GameSettings {
  volume: number;
  mouseSensitivity: number;
  quality: 'low' | 'medium' | 'high';
  showHints: boolean;
}

export const DEFAULT_SETTINGS: GameSettings = {
  volume: 0.5,
  mouseSensitivity: 1,
  quality: 'high',
  showHints: true,
};

export interface TitleScreen {
  root: HTMLElement;
  show(): void;
  hide(): void;
  isVisible(): boolean;
  /** True while any menu panel is capturing input. */
  handleKey(code: string): boolean;
}

export function createTitleScreen(
  version: string,
  hooks: TitleScreenHooks,
): TitleScreen {
  const root = el('div', 'le-title');
  let visible = true;
  let panel: 'main' | 'load' | 'settings' = 'main';

  // A soft vignette and gradient so the menu reads over any part of the world.
  root.appendChild(el('div', 'le-title-scrim'));

  const inner = el('div', 'le-title-inner');

  // --- wordmark ------------------------------------------------------------
  const brand = el('div', 'le-brand');
  const eyebrow = el('div', 'le-brand-eyebrow');
  eyebrow.textContent = 'Start with nothing';
  const wordmark = el('div', 'le-wordmark');
  // Drawn as two words so they can be weighted differently — a logo, not a label.
  const life = el('span', 'w1');
  life.textContent = 'LIFE';
  const empire = el('span', 'w2');
  empire.textContent = 'EMPIRE';
  wordmark.append(life, empire);
  const rule = el('div', 'le-brand-rule');
  const tagline = el('div', 'le-brand-tagline');
  tagline.textContent = 'Build your life. Own your future.';
  brand.append(eyebrow, wordmark, rule, tagline);
  inner.appendChild(brand);

  // --- menu ----------------------------------------------------------------
  const menu = el('div', 'le-menu');
  inner.appendChild(menu);

  const panelBox = el('div', 'le-title-panel');
  inner.appendChild(panelBox);

  const footer = el('div', 'le-title-footer');
  const versionTag = el('span');
  versionTag.textContent = `v${version}`;
  const hint = el('span', 'dim');
  hint.textContent = 'Maple Grove · a neighbourhood of twelve houses and one chance';
  footer.append(versionTag, hint);
  inner.appendChild(footer);

  root.appendChild(inner);

  function menuButton(
    label: string, sub: string, kind: 'primary' | 'ghost', onClick: () => void,
    disabled = false,
  ): HTMLButtonElement {
    const b = el('button', `le-menu-btn ${kind}`) as HTMLButtonElement;
    const l = el('span', 'l');
    l.textContent = label;
    const s = el('span', 's');
    s.textContent = sub;
    b.append(l, s);
    b.disabled = disabled;
    b.onclick = () => {
      if (disabled) return;
      hooks.sound?.ui('click');
      onClick();
    };
    return b;
  }

  function renderMain(): void {
    panel = 'main';
    panelBox.replaceChildren();
    const saves = hooks.listSaves();
    const resumable = saves.find((s) => !s.empty && !s.corrupt);

    menu.replaceChildren(
      menuButton('New Game', 'Start with $100 and a rented room', 'primary', () => {
        hooks.onNewGame();
      }),
      menuButton(
        'Continue',
        resumable ? resumable.detail : 'No saved game yet',
        'ghost',
        () => resumable && hooks.onContinue(resumable.slot),
        !resumable,
      ),
      menuButton('Load Game', 'Choose a save slot', 'ghost', () => renderLoad(),
        !saves.some((s) => !s.empty)),
      menuButton('Settings', 'Audio, controls and quality', 'ghost', () => renderSettings()),
      ...(hooks.onQuit
        ? [menuButton('Quit', 'Close the game', 'ghost', () => hooks.onQuit?.())]
        : []),
    );
  }

  function renderLoad(): void {
    panel = 'load';
    hooks.sound?.ui('open');
    menu.replaceChildren();
    const box = el('div', 'le-title-card');
    const head = el('div', 'le-title-card-head');
    head.textContent = 'Load game';
    box.appendChild(head);

    for (const save of hooks.listSaves()) {
      const row = el('div', `le-save-row${save.empty ? ' empty' : ''}`);
      const text = el('div');
      const name = el('div', 'n');
      name.textContent = save.label;
      const detail = el('div', 'd');
      detail.textContent = save.detail;
      text.append(name, detail);
      row.appendChild(text);
      if (!save.empty && !save.corrupt) {
        const load = el('button', 'le-menu-btn primary small') as HTMLButtonElement;
        load.textContent = 'Load';
        load.onclick = () => { hooks.sound?.ui('click'); hooks.onLoad(save.slot); };
        row.appendChild(load);
      }
      box.appendChild(row);
    }

    const back = el('button', 'le-menu-btn ghost small') as HTMLButtonElement;
    back.textContent = 'Back';
    back.onclick = () => { hooks.sound?.ui('close'); renderMain(); };
    box.appendChild(back);
    panelBox.replaceChildren(box);
  }

  function renderSettings(): void {
    panel = 'settings';
    hooks.sound?.ui('open');
    menu.replaceChildren();
    const settings = hooks.getSettings();

    const box = el('div', 'le-title-card');
    const head = el('div', 'le-title-card-head');
    head.textContent = 'Settings';
    box.appendChild(head);

    box.appendChild(slider('Volume', settings.volume, 0, 1, 0.05,
      (v) => hooks.setSettings({ volume: v }), (v) => `${Math.round(v * 100)}%`));
    box.appendChild(slider('Mouse sensitivity', settings.mouseSensitivity, 0.3, 2.5, 0.1,
      (v) => hooks.setSettings({ mouseSensitivity: v }), (v) => `${v.toFixed(1)}×`));
    box.appendChild(choice('Graphics quality',
      ['low', 'medium', 'high'], settings.quality,
      (v) => hooks.setSettings({ quality: v as GameSettings['quality'] })));
    box.appendChild(toggle('Show control hints', settings.showHints,
      (v) => hooks.setSettings({ showHints: v })));

    const note = el('div', 'le-title-note');
    note.textContent =
      'Lower quality reduces shadow resolution and draws fewer people. ' +
      'Press F11 in the desktop build for fullscreen.';
    box.appendChild(note);

    const back = el('button', 'le-menu-btn ghost small') as HTMLButtonElement;
    back.textContent = 'Back';
    back.onclick = () => { hooks.sound?.ui('close'); renderMain(); };
    box.appendChild(back);
    panelBox.replaceChildren(box);
  }

  renderMain();

  return {
    root,
    show(): void {
      visible = true;
      // Land at full opacity immediately. The fade belongs to leaving the menu,
      // not entering it, and on a slow machine a transitioning title screen is
      // a ghost image of a menu the player cannot read.
      root.style.transition = 'none';
      root.classList.remove('hidden');
      void root.offsetHeight;
      requestAnimationFrame(() => { root.style.transition = ''; });
      renderMain();
    },
    hide(): void {
      visible = false;
      root.classList.add('hidden');
    },
    isVisible: () => visible,
    handleKey(code: string): boolean {
      if (!visible) return false;
      if (code === 'Escape' && panel !== 'main') {
        renderMain();
        return true;
      }
      return true;
    },
  };
}

// ---------------------------------------------------------------- controls

function slider(
  label: string, value: number, min: number, max: number, step: number,
  onChange: (v: number) => void, format: (v: number) => string,
): HTMLElement {
  const row = el('div', 'le-setting');
  const head = el('div', 'le-setting-head');
  const name = el('span');
  name.textContent = label;
  const readout = el('span', 'v');
  readout.textContent = format(value);
  head.append(name, readout);

  const input = el('input', 'le-range') as HTMLInputElement;
  input.type = 'range';
  input.min = String(min);
  input.max = String(max);
  input.step = String(step);
  input.value = String(value);
  input.oninput = () => {
    const v = Number(input.value);
    readout.textContent = format(v);
    onChange(v);
  };

  row.append(head, input);
  return row;
}

function choice(
  label: string, options: string[], value: string, onChange: (v: string) => void,
): HTMLElement {
  const row = el('div', 'le-setting');
  const head = el('div', 'le-setting-head');
  const name = el('span');
  name.textContent = label;
  head.append(name);
  const group = el('div', 'le-choice');
  const buttons: HTMLButtonElement[] = [];
  for (const option of options) {
    const b = el('button') as HTMLButtonElement;
    b.textContent = option;
    b.classList.toggle('on', option === value);
    b.onclick = () => {
      for (const other of buttons) other.classList.remove('on');
      b.classList.add('on');
      onChange(option);
    };
    buttons.push(b);
    group.appendChild(b);
  }
  row.append(head, group);
  return row;
}

function toggle(label: string, value: boolean, onChange: (v: boolean) => void): HTMLElement {
  const row = el('div', 'le-setting');
  const head = el('div', 'le-setting-head');
  const name = el('span');
  name.textContent = label;
  const b = el('button', `le-toggle${value ? ' on' : ''}`) as HTMLButtonElement;
  b.textContent = value ? 'On' : 'Off';
  b.onclick = () => {
    value = !value;
    b.classList.toggle('on', value);
    b.textContent = value ? 'On' : 'Off';
    onChange(value);
  };
  head.append(name, b);
  row.append(head);
  return row;
}

export function describeSave(meta: {
  slot: string; day: number; clock: string; cash: number;
  businessName: string; netWorth: number; corrupt: boolean;
} | undefined, slot: string): SaveSlotInfo {
  const label = slot === 'autosave' ? 'Autosave' : `Slot ${slot.slice(-1)}`;
  if (!meta) return { slot, label, detail: 'Empty', empty: true, corrupt: false };
  if (meta.corrupt) {
    return { slot, label, detail: 'Unreadable save file', empty: false, corrupt: true };
  }
  return {
    slot, label,
    detail: `Day ${meta.day} · ${meta.clock} · ${formatMoney(meta.cash)} · ${meta.businessName}`,
    empty: false, corrupt: false,
  };
}

function el(tag: string, className = ''): HTMLElement {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}
